import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DeliveryDestinationNote } from 'customers';
import { NewCustomerDelivery } from '../../../interfaces/new-customer-requests.model';
import { NewCustomerService } from '../../../services/new-customer.service';
import { EMPTY_DELIVERY_NOTE, digitsOnlyControl, upperCaseControl } from '../../../utils/new-customer.utils';
import { NewCustomerDeliveryNotes } from '../new-customer-delivery-notes/new-customer-delivery-notes';

// Destino di consegna scritto a mano: uno solo per la proposta
@Component({
  selector: 'ord-new-customer-delivery-form',
  imports: [MatButtonModule, MatFormFieldModule, MatInputModule, NewCustomerDeliveryNotes, ReactiveFormsModule],
  templateUrl: './new-customer-delivery-form.html',
  styleUrl: './new-customer-delivery-form.scss',
})
export class NewCustomerDeliveryForm implements OnInit {
  readonly flow = inject(NewCustomerService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);

  readonly form = this.formBuilder.nonNullable.group({
    riferimento: ['', [Validators.required, Validators.maxLength(35)]],
    citta: ['', [Validators.required, Validators.maxLength(60)]],
    provincia: ['', Validators.required],
    cap: ['', Validators.required],
    indirizzo: ['', [Validators.required, Validators.maxLength(35)]],
  });

  nota: DeliveryDestinationNote = { ...EMPTY_DELIVERY_NOTE };

  ngOnInit(): void {
    // Riparte dal destino già scritto a mano, non da una sede dell'azienda
    const delivery = this.flow.delivery();
    if (delivery && delivery.branchId === null) {
      this.form.setValue({
        riferimento: delivery.riferimento ?? '',
        citta: delivery.citta,
        provincia: delivery.provincia,
        cap: delivery.cap,
        indirizzo: delivery.indirizzo,
      }, { emitEvent: false });
      this.nota = delivery.nota;
    }

    upperCaseControl(this.form.controls.provincia, this.destroyRef);
    digitsOnlyControl(this.form.controls.cap, this.destroyRef);

    // Si conserva subito: tornando indietro e poi avanti i dati restano
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.flow.setDelivery(this.delivery));
  }

  get delivery(): NewCustomerDelivery {
    const value = this.form.getRawValue();
    return {
      branchId: null,
      tipoSede: null,
      riferimento: value.riferimento.trim() || null,
      indirizzo: value.indirizzo.trim(),
      cap: value.cap.trim(),
      citta: value.citta.trim(),
      provincia: value.provincia.trim(),
      latitudine: null,
      longitudine: null,
      nota: this.nota,
    };
  }

  // Senza indirizzo si prosegue: la consegna va alla sede del committente
  get valid(): boolean {
    const delivery = this.delivery;
    return this.empty || (!!delivery.riferimento
      && !!delivery.indirizzo
      && !!delivery.citta
      && delivery.provincia.length === 2
      && delivery.cap.length === 5);
  }

  get empty(): boolean {
    const delivery = this.delivery;
    return !delivery.riferimento && !delivery.indirizzo && !delivery.cap && !delivery.citta && !delivery.provincia;
  }

  setNota(nota: DeliveryDestinationNote): void {
    this.nota = nota;
    this.flow.setDelivery(this.delivery);
  }

  continue(): void {
    if (!this.valid || this.flow.loading()) {
      return;
    }

    this.flow.setDelivery(this.delivery);
    this.flow.openZones().subscribe({
      next: step => {
        if (step) {
          this.flow.go(step);
        } else {
          this.snackBar.open('Nessuna zona disponibile per codificare il cliente', 'Chiudi', { duration: 5000 });
        }
      },
      error: (error: unknown) => {
        const message = this.flow.errorMessage(error);
        if (message) {
          this.snackBar.open(message, 'Chiudi', { duration: 5000 });
        }
      },
    });
  }
}
